import cn from 'classnames';
import { useTableSortingColumn } from './TableSorting';
import TableSortIcon from './TableSortIcon';
import Skeleton, { SkeletonShape } from '@components/Skeleton';
import { useMemo } from 'react';
import { TableColumn } from './TableColumns';

interface TableCellProps {
    isHead?: boolean;
    isFooter?: boolean;
    isSub?: boolean;
    hasSub?: boolean;
    sortingKey?: string;
    isLoading?: boolean;
    accentLevel?: TableColumn['accentLevel'];
    lockAccent?: TableColumn['lockAccent'];
    style?: React.CSSProperties;
    className?: string;
    children?: React.ReactNode;
    testId?: string;
    onClick?: () => void;
}

const TableCell: React.FC<TableCellProps> = ({
    isHead,
    isFooter,
    isSub,
    hasSub,
    isLoading,
    sortingKey,
    children,
    testId,
    accentLevel = 0,
    lockAccent,
    style,
    className,
    onClick,
    ...props
}) => {
    const { isSorted, direction, onSort } = useTableSortingColumn(isHead ? sortingKey : undefined);

    const onCellClick = useMemo(() => {
        if (isHead && sortingKey) {
            return onSort;
        }
        return onClick;
    }, [isHead, sortingKey, onSort, onClick]);

    return (
        <div
            className={cn(
                'TableCell',
                isHead && 'is-head',
                isFooter && 'is-footer',
                hasSub && 'is-super',
                isSub && 'is-sub',
                isHead && sortingKey && 'TableHeadCellSorting',
                `accent-${accentLevel + (isHead && !lockAccent || isFooter ? 1 : 0)}`,
                className,
            )}
            data-testid={testId}
            {...props}
            onClick={onCellClick}
            style={{
                ...style,
                userSelect: onCellClick ? 'none' : undefined,
            }}
        >
            {isHead && sortingKey && (
                <TableSortIcon isActive={isSorted} direction={direction} />
            )}
            {isLoading && !isHead && !isFooter ? (
                <Skeleton
                    shape={SkeletonShape.Text}
                    height={20}
                    radius={4}
                />
            ) : (
                children
            )}
        </div>
    );
};

export default TableCell;
